import { Box, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import { BRAND_COLOR } from "../constants";
import { exportLeadsByDayToCSV } from "../leadsCsvHelpers";

interface LeadsByDayChartProps {
  data: LeadsManagementData;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export default function LeadsByDayChart({ data }: LeadsByDayChartProps) {
  const { aggregations, goToFilteredLeads, refetch } = data;

  const countByDate = new Map(aggregations.byDay.map((item) => [item.date, item.count]));

  const today = new Date();
  const days: Date[] = [];
  for (let i = 13; i >= 0; i--) {
    days.push(new Date(today.getTime() - i * 86400000));
  }

  const series = days.map((day) => {
    const iso = toIsoDate(day);
    return {
      iso,
      label: `${MONTHS[day.getMonth()]} ${day.getDate()}`,
      count: countByDate.get(iso) ?? 0,
    };
  });

  return (
    <Paper variant="outlined" data-tracker="938" sx={{ mt: 2.5, p: "20px 24px", borderRadius: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography sx={{ flex: 1, fontSize: 18, fontWeight: 500, color: "#1a1a1a" }}>
          Leads by Day
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={refetch} sx={{ color: BRAND_COLOR, mr: 1.25 }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Export">
          <IconButton
            onClick={() => exportLeadsByDayToCSV({ rows: series })}
            sx={{ color: BRAND_COLOR }}
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={series} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid stroke="#eeeeee" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9e9e9e" }} interval={1} />
          <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#9e9e9e" }} />
          <RechartsTooltip />
          <Line
            type="linear"
            dataKey="count"
            stroke={BRAND_COLOR}
            strokeWidth={2.5}
            dot={(dotProps: any) => (
              <circle
                key={dotProps.payload.iso}
                cx={dotProps.cx}
                cy={dotProps.cy}
                r={6}
                fill="#ffffff"
                stroke={BRAND_COLOR}
                strokeWidth={2}
                style={{ cursor: "pointer" }}
                onClick={() =>
                  goToFilteredLeads({ start: dotProps.payload.iso, end: dotProps.payload.iso })
                }
              />
            )}
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );
}
